// @ts-nocheck - Victory Native XL types
import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  ActivityIndicator,
  Dimensions,
  Platform,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { Ionicons } from '@expo/vector-icons';
import { useSelector } from 'react-redux';
import { RootState } from '../store';
import {
  getRevenueSplitAnalytics,
  RevenueSplitAnalytics,
} from '../services/revenue-split-service';
import { CartesianChart, Bar, PolarChart, Pie } from 'victory-native';
import { LinearGradient, vec, RoundedRect, useFont } from '@shopify/react-native-skia';
import { SplitStatusBadge } from '../components/RevenueSplit';
import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { RootStackParamList } from '../navigation/stackTypes';

const { width } = Dimensions.get('window');

type NavigationProp = StackNavigationProp<RootStackParamList>;

type TabType = 'overview' | 'partners';

const RevenueSplitDashboard = () => {
  const navigation = useNavigation<NavigationProp>();
  const userSession = useSelector((state: RootState) => state.user.session);
  const [activeTab, setActiveTab] = useState<TabType>('overview');
  const [analytics, setAnalytics] = useState<RevenueSplitAnalytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Load font for axis labels - must be at top level per Rules of Hooks
  const axisFont = useFont(require('../../assets/fonts/Plus_Jakarta_Sans/static/PlusJakartaSans-Bold.ttf'), 10);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    if (!userSession?.id) {
      console.log('[RevenueSplitDashboard] No user session id, returning early');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const response = await getRevenueSplitAnalytics(userSession.id);

      if (response.status === 200 && response.payload) {
        setAnalytics(response.payload);
      } else {
        setError(response.payload?.message || 'Failed to load analytics');
      }
    } catch (err) {
      setError('An error occurred while loading analytics');
      console.error('Error fetching analytics:', err);
    } finally {
      setLoading(false);
    }
  };

  const renderOverviewTab = () => {
    if (!analytics) return null;

    const { summary, topPartners, topChargers, monthlyTrend } = analytics;

    // Prepare pie chart data
    const pieData = [
      { label: 'Your Revenue', value: summary.totalNetRevenue, color: '#51B868' },
      { label: 'Split Revenue', value: summary.totalSplitDeductions, color: '#3B82F6' },
    ];

    // Prepare monthly trends data for bar chart
    const barData = monthlyTrend.map((month, index) => {
      const date = new Date(month.month + '-01');
      const monthName = date.toLocaleDateString('en-US', { month: 'short' });
      return {
        x: index,
        gross: month.grossRevenue,
        net: month.netRevenue,
        label: monthName,
      };
    });

    return (
      <ScrollView style={styles.tabContent} showsVerticalScrollIndicator={false}>
        {/* Summary Cards */}
        <View style={styles.summaryCardsContainer}>
          <View style={styles.summaryCard}>
            <View style={styles.summaryCardHeader}>
              <Ionicons name="cash-outline" size={20} color="#3B82F6" />
              <Text style={styles.summaryCardLabel}>Partner Share This Month</Text>
            </View>
            <Text style={styles.summaryCardValue}>
              ${summary.totalSplitDeductions.toFixed(2)}
            </Text>
          </View>

          <View style={styles.summaryCard}>
            <View style={styles.summaryCardHeader}>
              <Ionicons name="trending-up-outline" size={20} color="#51B868" />
              <Text style={styles.summaryCardLabel}>Your Net Revenue</Text>
            </View>
            <Text style={[styles.summaryCardValue, { color: '#51B868' }]}>
              ${summary.totalNetRevenue.toFixed(2)}
            </Text>
          </View>

          <View style={styles.summaryCard}>
            <View style={styles.summaryCardHeader}>
              <Ionicons name="people-outline" size={20} color="#3B82F6" />
              <Text style={styles.summaryCardLabel}>Active Partners</Text>
            </View>
            <View style={styles.partnerCountContainer}>
              <View style={styles.partnerCountRow}>
                <Text style={styles.summaryCardValue}>{summary.activePartnerCount}</Text>
                <Text style={styles.partnerCountLabel}>Active</Text>
              </View>
              {summary.inactivePartnerCount > 0 && (
                <View style={styles.partnerCountRow}>
                  <Text style={styles.inactivePartnerCount}>{summary.inactivePartnerCount}</Text>
                  <Text style={styles.inactivePartnerLabel}>Inactive</Text>
                </View>
              )}
            </View>
          </View>

          <View style={styles.summaryCard}>
            <View style={styles.summaryCardHeader}>
              <Ionicons name="flash-outline" size={20} color="#3B82F6" />
              <Text style={styles.summaryCardLabel}>Chargers Shared</Text>
            </View>
            <Text style={styles.summaryCardValue}>{summary.chargerCount}</Text>
          </View>
        </View>

        {/* Revenue Distribution Pie Chart */}
        <View style={styles.chartSection}>
          <Text style={styles.chartTitle}>Revenue Distribution</Text>
          <View style={styles.chartContainer}>
            <PolarChart
              padding={6}
              data={pieData}
              labelKey="label"
              valueKey="value"
              colorKey="color"
              containerStyle={{ width: '100%', height: 250, backgroundColor: 'transparent' }}
              canvasStyle={{ backgroundColor: 'transparent' }}
            >
              <Pie.Chart
                innerRadius={60}
                padAngle={6}
                cornerRadius={4}
                animate={{ type: 'timing', duration: 400 }}
              >
                {({ slice }) => (
                  <>
                    <Pie.Slice />
                    <Pie.SliceAngularInset
                      angularInset={{ 
                        angularStrokeWidth: 6, 
                        angularStrokeColor: '#1A1D21' 
                      }}
                    />
                  </>
                )}
              </Pie.Chart>
            </PolarChart>
          </View>
          <View style={styles.chartLegend}>
            <View style={styles.legendItem}>
              <View style={[styles.legendDot, { backgroundColor: '#51B868' }]} />
              <Text style={styles.legendText}>
                Your Revenue: ${summary.totalNetRevenue.toFixed(2)}
              </Text>
            </View>
            <View style={styles.legendItem}>
              <View style={[styles.legendDot, { backgroundColor: '#3B82F6' }]} />
              <Text style={styles.legendText}>
                Split Revenue: ${summary.totalSplitDeductions.toFixed(2)}
              </Text>
            </View>
          </View>
        </View>

        {/* Top Partners */}
        <View style={styles.listSection}>
          <Text style={styles.sectionTitle}>Top Partners by Earnings</Text>
          {topPartners.length > 0 ? (
            topPartners.map((partner, index) => (
              <View key={partner.partnerId} style={styles.partnerCard}>
                <View style={styles.partnerRank}>
                  <Text style={styles.rankText}>#{index + 1}</Text>
                </View>
                <View style={styles.partnerInfo}>
                  <Text style={styles.partnerName}>{partner.partnerUsername}</Text>
                  {partner.partnerEmail && (
                    <Text style={styles.partnerEmail}>{partner.partnerEmail}</Text>
                  )}
                  <Text style={styles.partnerStats}>
                    {partner.chargerCount} charger{partner.chargerCount !== 1 ? 's' : ''}
                  </Text>
                </View>
                <View style={styles.partnerEarnings}>
                  <Text style={styles.earningsAmount}>
                    ${partner.totalEarnings.toFixed(2)}
                  </Text>
                  <Text style={styles.earningsLabel}>this month</Text>
                </View>
              </View>
            ))
          ) : (
            <Text style={styles.emptyText}>No partner data available</Text>
          )}
        </View>

        {/* Top Chargers */}
        <View style={styles.listSection}>
          <Text style={styles.sectionTitle}>Most Profitable Shared Chargers</Text>
          {topChargers.length > 0 ? (
            topChargers.map((charger, index) => (
              <View
                key={charger.chargerId}
                style={styles.chargerCard}
              >
                <View style={styles.chargerRank}>
                  <Text style={styles.rankText}>#{index + 1}</Text>
                </View>
                <View style={styles.chargerInfo}>
                  <Text style={styles.chargerName}>{charger.chargerName}</Text>
                  <Text style={styles.chargerStats}>
                    {charger.splitCount} split{charger.splitCount !== 1 ? 's' : ''}
                  </Text>
                </View>
                <View style={styles.chargerRevenue}>
                  <Text style={styles.revenueGross}>
                    ${charger.grossRevenue.toFixed(2)}
                  </Text>
                  <Text style={styles.revenueNet}>
                    Net: ${charger.netRevenue.toFixed(2)}
                  </Text>
                </View>
              </View>
            ))
          ) : (
            <Text style={styles.emptyText}>No charger data available</Text>
          )}
        </View>

        {/* Monthly Trends */}
        <View style={styles.chartSection}>
          <Text style={styles.chartTitle}>Gross vs. Net Earnings Over Time</Text>
          <View style={styles.chartContainer}>
            <CartesianChart
              data={barData}
              xKey="x"
              yKeys={['gross', 'net']}
              padding={15}
              domainPadding={{ top: 30, left: 50, right: 20 }}
              xAxis={{
                labelColor: '#E8E8E9',
                tickCount: barData.length,
                formatXLabel: (x) => {
                  const idx = Math.round(Number(x));
                  return barData[idx]?.label || '';
                },
                font: axisFont || undefined,
                labelOffset: 6,
                labelPosition: 'outset',
              }}
              yAxis={[{
                labelColor: '#E8E8E9',
                formatYLabel: (v) => `$${Number(v).toFixed(0)}`,
                yKeys: ['gross', 'net'],
                font: axisFont || undefined,
                labelOffset: 8,
                labelPosition: 'outset',
              }]}
            >
              {({ points, chartBounds }) => (
                <>
                  <RoundedRect
                    x={chartBounds.left}
                    y={chartBounds.top}
                    width={chartBounds.right - chartBounds.left}
                    height={chartBounds.bottom - chartBounds.top}
                    r={10}
                    color={'#1D2025'}
                  />
                  <Bar
                    points={points.gross}
                    chartBounds={chartBounds}
                    innerPadding={0.2}
                    barCount={barData.length}
                    color="#3B82F6"
                    animate={{ type: 'timing', duration: 1000 }}
                    roundedCorners={{ topLeft: 4, topRight: 4 }}
                  />
                  <Bar
                    points={points.net}
                    chartBounds={chartBounds}
                    innerPadding={0.2}
                    barCount={barData.length}
                    color="#51B868"
                    animate={{ type: 'timing', duration: 1000 }}
                    roundedCorners={{ topLeft: 4, topRight: 4 }}
                  />
                </>
              )}
            </CartesianChart>
          </View>

          <View style={styles.chartLegend}>
            <View style={styles.legendItem}>
              <View style={[styles.legendDot, { backgroundColor: '#3B82F6' }]} />
              <Text style={styles.legendText}>Gross Revenue</Text>
            </View>
            <View style={styles.legendItem}>
              <View style={[styles.legendDot, { backgroundColor: '#51B868' }]} />
              <Text style={styles.legendText}>Net Revenue</Text>
            </View>
          </View>
        </View>
      </ScrollView>
    );
  };

  const renderPartnersTab = () => {
    if (!analytics) return null;

    const { allPartners } = analytics;

    return (
      <ScrollView style={styles.tabContent} showsVerticalScrollIndicator={false}>
        <Text style={styles.tabDescription}>
          View all partners you're sharing revenue with across your chargers
        </Text>

        {allPartners.length > 0 ? (
          allPartners.map((partner) => (
            <View key={partner.partnerId} style={styles.partnerDetailCard}>
              <View style={styles.partnerDetailHeader}>
                <View style={styles.partnerDetailInfo}>
                  <Text style={styles.partnerDetailName}>
                    {String(partner.partnerUsername || 'Unknown')}
                  </Text>
                  {partner.partnerEmail && (
                    <Text style={styles.partnerDetailEmail}>
                      {String(partner.partnerEmail)}
                    </Text>
                  )}
                </View>
                <SplitStatusBadge
                  status={partner.status || 'ACTIVE'}
                  size="small"
                />
              </View>

              <View style={styles.partnerDetailStats}>
                <View style={styles.statItem}>
                  <Ionicons name="flash-outline" size={16} color="#3B82F6" />
                  <Text style={styles.statValue}>
                    {typeof partner.chargerCount === 'number' ? partner.chargerCount : 0}
                  </Text>
                  <Text style={styles.statLabel}>Chargers</Text>
                </View>

                <View style={styles.statDivider} />

                <View style={styles.statItem}>
                  <Ionicons name="cash-outline" size={16} color="#3B82F6" />
                  <Text style={styles.statValue}>
                    ${Number(partner.totalPaid || 0).toFixed(2)}
                  </Text>
                  <Text style={styles.statLabel}>Total Paid</Text>
                </View>

                <View style={styles.statDivider} />

                <View style={styles.statItem}>
                  <Ionicons 
                    name={partner.status === 'INACTIVE' ? 'lock-closed-outline' : 'calendar-outline'} 
                    size={16} 
                    color={partner.status === 'INACTIVE' ? '#F59E0B' : '#3B82F6'} 
                  />
                  <Text style={[
                    styles.statValue,
                    partner.status === 'INACTIVE' && { color: '#F59E0B' }
                  ]}>
                    ${Number(
                      partner.status === 'INACTIVE' 
                        ? partner.totalWithheld 
                        : partner.totalPending
                    ).toFixed(2) || '0.00'}
                  </Text>
                  <Text style={styles.statLabel}>
                    {partner.status === 'INACTIVE' ? 'Withheld' : 'Pending'}
                  </Text>
                </View>
              </View>

              {partner.chargers && Array.isArray(partner.chargers) && partner.chargers.length > 0 && (
                <View style={styles.partnerChargers}>
                  <Text style={styles.partnerChargersTitle}>Chargers:</Text>
                  {partner.chargers.map((charger) => (
                    <View
                      key={charger.chargerId}
                      style={styles.partnerChargerItem}
                    >
                      <Text style={styles.partnerChargerName}>
                        {String(charger.chargerName || 'Unknown')}
                      </Text>
                      <Text style={styles.partnerChargerSplit}>
                        {Number(charger.splitPercentage || 0).toFixed(1)}%
                      </Text>
                    </View>
                  ))}
                </View>
              )}
            </View>
          ))
        ) : (
          <View style={styles.emptyState}>
            <Ionicons name="people-outline" size={64} color="#8D8E90" />
            <Text style={styles.emptyStateTitle}>No Partners Yet</Text>
            <Text style={styles.emptyStateText}>
              Start sharing revenue by adding partners to your chargers
            </Text>
          </View>
        )}
      </ScrollView>
    );
  };

  if (loading) {
    return (
      <SafeAreaView style={styles.container} edges={['top']}>
        <View style={styles.header}>
          <TouchableOpacity
            onPress={() => navigation.goBack()}
            style={styles.backButton}
          >
            <Ionicons name="arrow-back" size={24} color="#fff" />
          </TouchableOpacity>
          <Text style={styles.headerTitle}>Revenue Split Dashboard</Text>
        </View>
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color="#3B82F6" />
          <Text style={styles.loadingText}>Loading analytics...</Text>
        </View>
      </SafeAreaView>
    );
  }

  if (error) {
    return (
      <SafeAreaView style={styles.container} edges={['top']}>
        <View style={styles.header}>
          <TouchableOpacity
            onPress={() => navigation.goBack()}
            style={styles.backButton}
          >
            <Ionicons name="arrow-back" size={24} color="#fff" />
          </TouchableOpacity>
          <Text style={styles.headerTitle}>Revenue Split Dashboard</Text>
        </View>
        <View style={styles.errorContainer}>
          <Ionicons name="alert-circle-outline" size={64} color="#F97777" />
          <Text style={styles.errorText}>{error}</Text>
          <TouchableOpacity style={styles.retryButton} onPress={fetchAnalytics}>
            <Text style={styles.retryButtonText}>Retry</Text>
          </TouchableOpacity>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container} edges={['top']}>
      <View style={styles.header}>
        <TouchableOpacity
          onPress={() => navigation.goBack()}
          style={styles.backButton}
        >
          <Ionicons name="arrow-back" size={24} color="#fff" />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>Revenue Split Dashboard</Text>
        <TouchableOpacity onPress={fetchAnalytics} style={styles.refreshButton}>
          <Ionicons name="refresh" size={24} color="#fff" />
        </TouchableOpacity>
      </View>

      {/* Tabs */}
      <View style={styles.tabsContainer}>
        <TouchableOpacity
          style={[styles.tab, activeTab === 'overview' && styles.activeTab]}
          onPress={() => setActiveTab('overview')}
        >
          <Text
            style={[styles.tabText, activeTab === 'overview' && styles.activeTabText]}
          >
            Overview
          </Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={[styles.tab, activeTab === 'partners' && styles.activeTab]}
          onPress={() => setActiveTab('partners')}
        >
          <Text
            style={[styles.tabText, activeTab === 'partners' && styles.activeTabText]}
          >
            Partners
          </Text>
        </TouchableOpacity>
      </View>

      {/* Tab Content */}
      {activeTab === 'overview' ? renderOverviewTab() : renderPartnersTab()}
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#1A1D21',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 12,
    backgroundColor: '#292D33',
    borderBottomWidth: 1,
    borderBottomColor: '#1A1D23',
  },
  backButton: {
    padding: 4,
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: '#fff',
    flex: 1,
    textAlign: 'center',
  },
  refreshButton: {
    padding: 4,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    color: '#8D8E90',
    fontSize: 16,
    marginTop: 12,
  },
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 30,
  },
  errorText: {
    color: '#F97777',
    fontSize: 16,
    textAlign: 'center',
    marginTop: 16,
    marginBottom: 24,
  },
  retryButton: {
    backgroundColor: '#3B82F6',
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 8,
  },
  retryButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '600',
  },
  tabsContainer: {
    flexDirection: 'row',
    backgroundColor: '#292D33',
    paddingHorizontal: 16,
    paddingTop: 8,
  },
  tab: {
    flex: 1,
    paddingVertical: 12,
    alignItems: 'center',
    borderBottomWidth: 2,
    borderBottomColor: 'transparent',
  },
  activeTab: {
    borderBottomColor: '#3B82F6',
  },
  tabText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#8D8E90',
  },
  activeTabText: {
    color: '#3B82F6',
  },
  tabContent: {
    flex: 1,
    padding: 16,
  },
  tabDescription: {
    color: '#8D8E90',
    fontSize: 14,
    marginBottom: 16,
    lineHeight: 20,
  },
  summaryCardsContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginBottom: 20,
    gap: 12,
  },
  summaryCard: {
    backgroundColor: '#292D33',
    borderRadius: 12,
    padding: 16,
    width: (width - 44) / 2,
    minHeight: 100,
  },
  summaryCardHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 12,
  },
  summaryCardLabel: {
    color: '#E8E8E9',
    fontSize: 12,
    fontWeight: '500',
    flex: 1,
  },
  summaryCardValue: {
    color: '#3B82F6',
    fontSize: 24,
    fontWeight: '700',
  },
  partnerCountContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 8,
  },
  partnerCountRow: {
    alignItems: 'center',
  },
  partnerCountLabel: {
    color: '#8D8E90',
    fontSize: 10,
    fontWeight: '500',
    marginTop: 2,
  },
  inactivePartnerCount: {
    color: '#8D8E90',
    fontSize: 18,
    fontWeight: '600',
  },
  inactivePartnerLabel: {
    color: '#8D8E90',
    fontSize: 10,
    fontWeight: '500',
    marginTop: 2,
  },
  chartSection: {
    backgroundColor: '#292D33',
    borderRadius: 12,
    padding: 16,
    marginBottom: 20,
  },
  chartTitle: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '700',
    marginBottom: 16,
  },
  chartContainer: {
    width: width - 40, // Full width minus padding
    height: 280,
    alignSelf: 'center',
  },
  chartLegend: {
    flexDirection: 'row',
    justifyContent: 'center',
    flexWrap: 'wrap',
    gap: 16,
    marginTop: 12,
  },
  legendItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  legendDot: {
    width: 12,
    height: 12,
    borderRadius: 6,
  },
  legendText: {
    color: '#E8E8E9',
    fontSize: 12,
  },
  listSection: {
    marginBottom: 20,
  },
  sectionTitle: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '700',
    marginBottom: 12,
  },
  partnerCard: {
    backgroundColor: '#292D33',
    borderRadius: 12,
    padding: 12,
    marginBottom: 8,
    flexDirection: 'row',
    alignItems: 'center',
  },
  partnerRank: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: '#3B82F6',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  rankText: {
    color: '#fff',
    fontSize: 12,
    fontWeight: '700',
  },
  partnerInfo: {
    flex: 1,
  },
  partnerName: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '600',
  },
  partnerEmail: {
    color: '#8D8E90',
    fontSize: 11,
    marginTop: 2,
  },
  partnerStats: {
    color: '#8D8E90',
    fontSize: 11,
    marginTop: 4,
  },
  partnerEarnings: {
    alignItems: 'flex-end',
  },
  earningsAmount: {
    color: '#51B868',
    fontSize: 16,
    fontWeight: '700',
  },
  earningsLabel: {
    color: '#8D8E90',
    fontSize: 10,
    marginTop: 2,
  },
  chargerCard: {
    backgroundColor: '#292D33',
    borderRadius: 12,
    padding: 12,
    marginBottom: 8,
    flexDirection: 'row',
    alignItems: 'center',
  },
  chargerRank: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: '#3B82F6',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  chargerInfo: {
    flex: 1,
  },
  chargerName: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '600',
  },
  chargerStats: {
    color: '#8D8E90',
    fontSize: 11,
    marginTop: 4,
  },
  chargerRevenue: {
    alignItems: 'flex-end',
    marginRight: 8,
  },
  revenueGross: {
    color: '#3B82F6',
    fontSize: 14,
    fontWeight: '700',
  },
  revenueNet: {
    color: '#51B868',
    fontSize: 11,
    marginTop: 2,
  },
  partnerDetailCard: {
    backgroundColor: '#292D33',
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
  },
  partnerDetailHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 16,
  },
  partnerDetailInfo: {
    flex: 1,
    marginRight: 12,
  },
  partnerDetailName: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '700',
  },
  partnerDetailEmail: {
    color: '#8D8E90',
    fontSize: 13,
    marginTop: 4,
  },
  partnerDetailStats: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    paddingVertical: 12,
    borderTopWidth: 1,
    borderBottomWidth: 1,
    borderColor: '#1A1D23',
    marginBottom: 12,
  },
  statItem: {
    alignItems: 'center',
    flex: 1,
  },
  statValue: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '700',
    marginTop: 4,
  },
  statLabel: {
    color: '#8D8E90',
    fontSize: 11,
    marginTop: 2,
  },
  statDivider: {
    width: 1,
    backgroundColor: '#1A1D23',
  },
  partnerChargers: {
    marginTop: 4,
  },
  partnerChargersTitle: {
    color: '#8D8E90',
    fontSize: 12,
    fontWeight: '600',
    textTransform: 'uppercase',
    marginBottom: 8,
  },
  partnerChargerItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: '#1A1D23',
    borderRadius: 8,
    padding: 10,
    marginBottom: 6,
  },
  partnerChargerName: {
    color: '#E8E8E9',
    fontSize: 14,
    flex: 1,
  },
  partnerChargerSplit: {
    color: '#3B82F6',
    fontSize: 14,
    fontWeight: '700',
  },
  emptyText: {
    color: '#8D8E90',
    fontSize: 14,
    textAlign: 'center',
    paddingVertical: 20,
  },
  emptyState: {
    alignItems: 'center',
    paddingVertical: 60,
  },
  emptyStateTitle: {
    color: '#fff',
    fontSize: 18,
    fontWeight: '700',
    marginTop: 16,
    marginBottom: 8,
  },
  emptyStateText: {
    color: '#8D8E90',
    fontSize: 14,
    textAlign: 'center',
    paddingHorizontal: 40,
    lineHeight: 20,
  },
});

export default RevenueSplitDashboard;

